import axios from "axios";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import Actions from "../../../components/Common/Actions";
import Breadcrumbs from "../../../components/Common/Breadcrumbs/AntdBreadcrumbs";
import FilledButton from "../../../components/Common/Buttons/FilledButton";
import CommonFilterModal from "../../../components/Common/CommonFilterModal";
import DoctorsFilterOptions from "../../../components/Common/CommonFilterOptions";
import ListPageLayout from "../../../components/Layout";
import Deletepopup from "../../../components/Modal/Deletepopup";
import ExcelDownload from "../../../components/Utils/excelDownload";
import useDebouncedSearch from "../../../components/Utils/hooks/useDebouncedSearch";
import useDeleteHandler from "../../../components/Utils/hooks/useDeleteHandler";
import usePageChange from "../../../components/Utils/hooks/usePageChange";
import usePaginatedData from "../../../components/Utils/hooks/usePaginatedData";
import useSortHandler from "../../../components/Utils/hooks/useSortHandler";
import ExcelDownloadModal from "../../../Pages/Masters/Common/excelDownloadAccess";

const Timings = ({ data: timings }) => {
  const daysOfWeek = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  const formattedHours = {};

  daysOfWeek.forEach((day) => {
    if (timings[`${day}Closed`]) {
      formattedHours[day] = `Closed`;
    } else if (timings?.[`${day}Available`]) {
      formattedHours[day] = `24 Hours Available`;
    } else {
      const timeSlots = [];

      const morningStartTime = timings[`${day}_start_timing`];
      const morningEndTime = timings[`${day}_end_timing`];
      const eveningStartTime = timings[`${day}_evening_start_timing`];
      const eveningEndTime = timings[`${day}_evening_end_timing`];

      const formatTime = (time) => {
        if (!time || time === "Invalid date") return "";
        return time.length <= 8 ? time : moment(time).format("h:mm A");
      };

      if (morningStartTime && morningEndTime) {
        const formattedMorning = `${formatTime(
          morningStartTime
        )} to ${formatTime(morningEndTime)}`;
        timeSlots.push(`Morning: ${formattedMorning}`);
      }

      if (eveningStartTime && eveningEndTime) {
        const formattedEvening = `${formatTime(
          eveningStartTime
        )} to ${formatTime(eveningEndTime)}`;
        timeSlots.push(`Evening: ${formattedEvening}`);
      }

      formattedHours[day] =
        timeSlots.length > 0 ? timeSlots.join(" | ") : "Closed";
    }
  });

  return (
    <ul>
      {Object.keys(formattedHours)?.map((day, index) => (
        <li key={index}>
          {day}: {formattedHours[day]}
        </li>
      ))}
    </ul>
  );
};

const DoctorsClinicProfile = () => {
  const navigate = useNavigate();
  const addNewClinicProfile = {
    id: "addnewpost",
    onClick: () => navigate("/doctor-clinic-profile-create"),
    style: { width: "240px" },
    label: "Add New Clinic / Hosiptal Profile",
  };
  // Sorting
  const [sort, setSort] = useState("");
  const [sortName, setSortName] = useState("Newest First");
  const handleSortClick = useSortHandler(setSort, setSortName);
  const [filters, setFilters] = useState({});
  const [loader, setLoader] = useState(true);

  // Search
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedSearch(setSearch, () =>
    setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 }))
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Filter states
  const [selectedValues, setSelectedValues] = useState({
    "Clinic Specialty": [],
    "Clinic City": [],
    "Establish Type": [],
  });
  const [tempSelectedValues, setTempSelectedValues] = useState({
    "Clinic Specialty": [],
    Status: [],
  });

  const [selectedCategory, setSelectedCategory] = useState("Clinic Specialty");
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);

  // Delete state
  const [deleteId, setDeleteId] = useState("");
  const [showDelete, setShowDelete] = useState(false);

  function formatCamelCase(camelCaseStr) {
    if (!camelCaseStr) return "";

    // Insert a space before all caps and capitalize each word
    return camelCaseStr
      .replace(/([A-Z])/g, " $1") // Add space before capital letters
      .replace(/^./, (str) => str.toUpperCase()) // Capitalize the first letter
      .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize each word
  }

  // Pagination
  const { records, totalCount, pagination, setPagination, fetchData } =
    usePaginatedData({
      apiUrl: `${process.env.REACT_APP_API_URL}clinic-profiles/list`,
      initialParams: {
        sort,
        searchText: search,
        selectedValues,
      },
    });

  const handlePageChange = usePageChange(pagination, setPagination);
  const handleDelete = useDeleteHandler(
    "doctors-profile",
    fetchData,
    setShowDelete
  );

  useEffect(() => {
    async function fetchData() {
      let tempFilters = {};
      const temp = await axios?.post(
        `${process.env.REACT_APP_API_URL}clinic-profiles/filter-options`,
        { noLimit: true }
      );
      await Object.entries(temp?.data)?.map(async (data, index) => {
        const key = await formatCamelCase(data[0]);
        tempFilters[key] = await data[1]?.map((val, index) => val?.name);
      });
      await setFilters(tempFilters);
      await setSelectedCategory(Object.keys(tempFilters)[0]);
    }
    fetchData();
  }, []);

  useEffect(() => {
    setLoader(true);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.skip, pagination.limit, sort, search, selectedValues]);

  // const getsrcValue = (value) => {
  //   if (typeof value === "string" && value.length)
  //     return process.env.REACT_APP_API_URL + "clinic-profile/" + value;
  // };

  const getsrcValue = (value) => {
    if (!value) return "";

    // If value is already a full URL (e.g. DigitalOcean or HTTPS link)
    if (value.startsWith("http://") || value.startsWith("https://")) {
      return value;
    }

    // Otherwise, assume it's a local filename (old uploads)
    return `${process.env.REACT_APP_API_URL}clinic-profile/${value}`;
  };

  const handleActive = (id, status) => {
    setLoader(true);
    axios
      .post(`${process.env.REACT_APP_API_URL}doctors-profile/update/${id}`, {
        active: !status,
      })
      .then((result) => {
        fetchData();
      });
  };

  const onClickDownload = async () => {
    try {
      const formattedRecords = records?.map((record, index) => ({
        "S.No": index + 1,
        "Time of Registration": record.createdAt
          ? new Date(record.createdAt).toLocaleTimeString()
          : "",
        "Date of Registration": record.createdAt
          ? new Date(record.createdAt).toLocaleDateString()
          : "",
        "Name of Clinic / Hosiptal": record.clinic_name || "",
        "Clinic Speciality":
          record.clinic_specialty_status?.clinic_speciality || "",
        "Establishment Type": record.organisationType || "",
        "Concerned Doctor's Name":
          record.doctors?.map((doctor) => doctor.doctor_name).join(", ") || "",
        City: record.city?.city || "",
        "Location of Clinic / Hospital": record.clinic_location?.locality || "",
        "Pincode of Clinic / Hospital": record.pincode || "",
        Status: record.active ? "True" : "False" || "",
        "License / Registration Number": record.license_no || "",
        Timings: record.timings ? JSON.stringify(record.timings) : "",
      }));

      ExcelDownload(formattedRecords, "", "DoctorClinicProfile.xlsx");
      // console.log("File downloaded successfully.");
    } catch (err) {
      console.error(
        "Error downloading the file while getting all Doctor registration records",
        err,
        "err.message",
        err?.message
      );
      alert(err?.message || "An error occurred while downloading the file.");
    }
  };

  const columns = [
    {
      title: "S.No",
      render: (text, record, index) =>
        Number(pagination.skip || 0) + (index + 1),
      key: "serial",
      width: 80,
      fixed: "left",
    },
    {
      title: "Name of Clinic / Hospital",
      dataIndex: "clinic_name",
      key: "clinic_name",
      fixed: "left",
    },
    {
      title: "Clinic Speciality",
      dataIndex: "clinic_specialty_status",
      key: "clinic_specialty_status",
      render: (text) => text?.clinic_speciality,
    },
    {
      title: "Establishment Type",
      dataIndex: "organisationType",
      key: "organisationType",
    },
    {
      title: "Concerned Doctor's Name",
      render: (text, record) => (
        <ul>
          {record.doctors?.map((data) => (
            <li key={data?.inx}>{data?.doctor_name}</li>
          ))}
        </ul>
      ),
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      render: (text) => text?.city,
    },
    {
      title: "Location of Clinic / Hospital",
      dataIndex: "clinic_location",
      key: "clinic_location",
      render: (text) => text?.locality,
    },
    {
      title: "Pincode of Clinic / Hospital",
      dataIndex: "pincode",
      key: "pincode",
    },
    {
      title: "Clinic / Hospital Photos",
      render: (text, record) => (
        <div>
          {record.photos?.map((data, key) => (
            <img
              src={getsrcValue(data)}
              key={key}
              className="doctorProfileImage"
              alt="clinic"
            />
          ))}
        </div>
      ),
    },
    {
      title: "Status",
      render: (text, record) => (record.active ? "Verified" : "Not Verified"),
    },
    {
      title: "License / Registration Number",
      dataIndex: "license_no",
      key: "license_no",
    },
    {
      title: "Logo of Clinic / Hospital",
      render: (text, record) => (
        <img
          src={getsrcValue(record.logo)}
          className="doctorProfileImage"
          alt="logo"
        />
      ),
    },
    {
      title: "Timings",
      render: (text, record) => <Timings data={record?.timings} />,
    },
    {
      title: "Google Map Location Mark",
      dataIndex: "map_location",
      key: "map_location",
    },

    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="d-flex align-items-center">
          <Actions
            isActive={true}
            recordActiveStatus={record.active}
            onClickActive={() => handleActive(record._id, record.active)}
            isEdit={true}
            isDelete={true}
            onClickDelete={() => {
              setDeleteId(record._id);
              setShowDelete(true);
            }}
            onClickEdit={() => navigate(`/clinic-profile-edit/${record._id}`)}
          />
        </div>
      ),
    },
  ];

  const dataSource = records?.map((el) => ({ ...el, key: el._id }));

  const breadcrumbsList = useMemo(
    () => [
      {
        text: "Doctors",
        link: "/add-new-doctors",
      },
      {
        text: "Clinic Profiles",
        link: "",
      },
    ],
    []
  );

  useEffect(() => {
    records && setTimeout(() => setLoader(false));
  }, [records]);

  return (
    <>
      <ListPageLayout
        breadcrumbs={
          <Breadcrumbs
            className="healthsyMasterBreadCrumbs"
            breadcrumbsList={breadcrumbsList}
          />
        }
        addButton={<FilledButton props={addNewClinicProfile} />}
        filters={
          <DoctorsFilterOptions
            limit={pagination.limit}
            sortName={sortName}
            search={search}
            setFilterShow={() => setIsFilterModalVisible(true)}
            // setSortShow={setSortShow}
            onChange={debouncedSearch}
            setLimit={(val) =>
              setPagination((prev) => ({
                ...prev,
                limit: val,
                skip: 0,
                currentPage: 1,
              }))
            }
            downloadExcel={true}
            onClickDownload={() => setIsModalOpen(true)}
            handleSortClick={handleSortClick}
          />
        }
        tableProps={{
          columns,
          dataSource,
        }}
        totalCount={totalCount}
        pagination={pagination}
        onPageChange={(selected) => handlePageChange({ selected })}
        loader={loader}
      >
        {/* Optional children like modals */}
        {showDelete && (
          <Deletepopup
            handleDelete={() => handleDelete(deleteId)}
            id={deleteId}
            handleClose={() => setShowDelete(false)}
          />
        )}

        <CommonFilterModal
          visible={isFilterModalVisible}
          onClose={() => setIsFilterModalVisible(false)}
          onApply={() => {
            setSelectedValues(tempSelectedValues);
            setIsFilterModalVisible(false);
            setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 }));
            fetchData();
          }}
          onReset={() => {
            setTempSelectedValues({
              "Clinic Specialty": [],
              "Clinic City": [],
              "Establish Type": [],
            });
            setSelectedValues({
              "Clinic Specialty": [],
              "Clinic City": [],
              "Establish Type": [],
            });
            setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 }));
            fetchData();
          }}
          filters={filters}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          selectedValues={tempSelectedValues}
          setSelectedValues={setTempSelectedValues}
        />
      </ListPageLayout>
      <ExcelDownloadModal
        onClickDownload={onClickDownload}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
      />
    </>
  );
};

export default DoctorsClinicProfile;
